import { FormEvent, useEffect, useState } from 'react'
import { AuditLogPanel } from '@/components/settings/AuditLogPanel'
import { PromptTemplatesPanel } from '@/components/settings/PromptTemplatesPanel'
import { ProviderApiKeysPanel } from '@/components/settings/ProviderApiKeysPanel'
import {
  settingsApi,
  DEFAULT_SCAN_SETTINGS,
  DEFAULT_SCAN_SCHEDULE,
  DEFAULT_RANKING_WEIGHTS,
  DEFAULT_CLASSIFICATION,
  type ProviderListItem,
  type ScanSettings,
  type ScanSchedule,
  type RankingWeights,
  type ThemeMode,
  type ClassificationSettings,
} from '@/lib/api/settings'
import { useTheme } from '@/components/theme/ThemeProvider'
import { Button } from '@/components/ui/button'

const CRON_PRESETS = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Daily at 2am', value: '0 2 * * *' },
  { label: 'Weekly (Mon 2am)', value: '0 2 * * 1' },
]

const WEIGHT_FIELDS: Array<{ key: keyof RankingWeights; label: string }> = [
  { key: 'priceScore', label: 'Price' },
  { key: 'qualityScore', label: 'Quality' },
  { key: 'userScore', label: 'User rating' },
  { key: 'contextScore', label: 'Context window' },
  { key: 'featureScore', label: 'Features' },
  { key: 'freedomScore', label: 'Freedom' },
  { key: 'speedScore', label: 'Speed' },
  { key: 'externalScore', label: 'External' },
  { key: 'survivalScore', label: 'Survival' },
]

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

export function SettingsPage() {
  const { theme, setTheme } = useTheme()
  const [providers, setProviders] = useState<ProviderListItem[]>([])
  const [scanSettings, setScanSettings] = useState<ScanSettings>(DEFAULT_SCAN_SETTINGS)
  const [scanSchedule, setScanSchedule] = useState<ScanSchedule>(DEFAULT_SCAN_SCHEDULE)
  const [rankingWeights, setRankingWeights] = useState<RankingWeights>(DEFAULT_RANKING_WEIGHTS)
  const [classification, setClassification] = useState<ClassificationSettings>(DEFAULT_CLASSIFICATION)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    settingsApi.listProviders().then(setProviders).catch(() => setProviders([]))
    settingsApi.getScanSettings().then(setScanSettings).catch(() => setScanSettings(DEFAULT_SCAN_SETTINGS))
    settingsApi.getScanSchedule().then(setScanSchedule).catch(() => setScanSchedule(DEFAULT_SCAN_SCHEDULE))
    settingsApi.getRankingWeights().then(setRankingWeights).catch(() => setRankingWeights(DEFAULT_RANKING_WEIGHTS))
    settingsApi.getClassification().then(setClassification).catch(() => setClassification(DEFAULT_CLASSIFICATION))
  }, [])

  const handleSaveScanSettings = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setMessage(null)
    try {
      const saved = await settingsApi.saveScanSettings(scanSettings)
      setScanSettings(saved)
      setMessage('Scan settings saved.')
    } catch {
      setMessage('Failed to save scan settings.')
    } finally {
      setBusy(false)
    }
  }

  const handleSaveScanSchedule = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setMessage(null)
    try {
      const saved = await settingsApi.saveScanSchedule(scanSchedule)
      setScanSchedule(saved)
      setMessage('Scan schedule saved.')
    } catch {
      setMessage('Failed to save scan schedule.')
    } finally {
      setBusy(false)
    }
  }

  const handleSaveRankingWeights = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setMessage(null)
    try {
      const saved = await settingsApi.saveRankingWeights(rankingWeights)
      setRankingWeights(saved)
      setMessage('Ranking weights saved.')
    } catch {
      setMessage('Failed to save ranking weights.')
    } finally {
      setBusy(false)
    }
  }

  const handleSaveClassification = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setMessage(null)
    try {
      const saved = await settingsApi.saveClassification(classification)
      setClassification(saved)
      setMessage('Classification settings saved.')
    } catch {
      setMessage('Failed to save classification settings.')
    } finally {
      setBusy(false)
    }
  }

  const handleThemeChange = (nextTheme: ThemeMode) => {
    setTheme(nextTheme)
    setMessage('Appearance updated.')
  }

  return (
    <div className="flex h-full flex-col" data-testid="settings-page">
      <div className="border-b border-border px-4 py-3">
        <h1 className="text-lg font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">API keys, scan configuration, and audit log</p>
      </div>

      {message ? (
        <p className="px-4 py-2 text-sm text-muted-foreground" data-testid="settings-message">
          {message}
        </p>
      ) : null}

      <div className="grid gap-4 overflow-auto p-4 md:grid-cols-2">
        <ProviderApiKeysPanel
          providers={providers}
          onProvidersChange={setProviders}
          busy={busy}
          setBusy={setBusy}
          setMessage={setMessage}
        />

        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">Scan Parameters</h2>
          <form onSubmit={handleSaveScanSettings} className="mt-4 space-y-3">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Target models per category</span>
              <input
                type="number"
                min={1}
                max={100}
                value={scanSettings.targetModelsPerCategory}
                onChange={(event) =>
                  setScanSettings((current) => ({
                    ...current,
                    targetModelsPerCategory: Number(event.target.value),
                  }))
                }
                className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2"
                data-testid="settings-scan-target"
              />
            </label>

            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Max active models per category</span>
              <input
                type="number"
                min={1}
                max={200}
                value={scanSettings.maxActivePerCategory}
                onChange={(event) =>
                  setScanSettings((current) => ({
                    ...current,
                    maxActivePerCategory: Number(event.target.value),
                  }))
                }
                className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2"
                data-testid="settings-scan-max-active"
              />
            </label>

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scanSettings.freeOnly}
                onChange={(event) =>
                  setScanSettings((current) => ({ ...current, freeOnly: event.target.checked }))
                }
              />
              Free models only
            </label>

            <Button type="submit" size="sm" disabled={busy} className="min-h-11 w-full sm:w-auto" data-testid="settings-save-scan">
              Save scan settings
            </Button>
          </form>
        </section>

        <section className="rounded-lg border border-border bg-card p-4" data-testid="settings-scan-schedule">
          <h2 className="text-sm font-semibold">Scan Schedule</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Configure when ModelScout automatically scans for new models.
          </p>
          <form onSubmit={handleSaveScanSchedule} className="mt-4 space-y-3">
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Preset</span>
              <div className="flex flex-wrap gap-2">
                {CRON_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setScanSchedule((s) => ({ ...s, scheduleCron: preset.value }))}
                    className={`rounded-md border px-3 py-1 text-xs transition-colors ${
                      scanSchedule.scheduleCron === preset.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background hover:bg-muted'
                    }`}
                    data-testid={`scan-schedule-preset-${preset.value}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Cron expression</span>
              <input
                type="text"
                value={scanSchedule.scheduleCron}
                onChange={(event) =>
                  setScanSchedule((s) => ({ ...s, scheduleCron: event.target.value }))
                }
                placeholder="0 2 * * *"
                className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                data-testid="settings-scan-cron"
              />
            </label>

            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={scanSchedule.autoEnabled}
                onChange={(event) =>
                  setScanSchedule((s) => ({ ...s, autoEnabled: event.target.checked }))
                }
                data-testid="settings-scan-auto-enabled"
              />
              Enable automatic scans
            </label>

            <Button
              type="submit"
              size="sm"
              disabled={busy}
              className="min-h-11 w-full sm:w-auto"
              data-testid="settings-save-scan-schedule"
            >
              Save schedule
            </Button>
          </form>
        </section>

        <section className="rounded-lg border border-border bg-card p-4" data-testid="settings-appearance">
          <h2 className="text-sm font-semibold">Appearance</h2>
          <p className="mt-1 text-xs text-muted-foreground">Theme preference syncs across devices.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {THEME_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleThemeChange(option.value)}
                className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                  theme === option.value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background hover:bg-muted'
                }`}
                data-testid={`settings-theme-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-border bg-card p-4" data-testid="settings-classification">
          <h2 className="text-sm font-semibold">Model Classification</h2>
          <form onSubmit={handleSaveClassification} className="mt-4 space-y-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={classification.llmEnabled}
                onChange={(event) =>
                  setClassification((current) => ({ ...current, llmEnabled: event.target.checked }))
                }
                data-testid="settings-llm-enabled"
              />
              Enable LLM-assisted classification during scans
            </label>
            <Button type="submit" size="sm" disabled={busy} data-testid="settings-save-classification">
              Save classification
            </Button>
          </form>
        </section>

        <section className="rounded-lg border border-border bg-card p-4" data-testid="settings-ranking-weights">
          <h2 className="text-sm font-semibold">Ranking Weights</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Adjust how each score factor contributes to category ranking.
          </p>
          <form onSubmit={handleSaveRankingWeights} className="mt-4 space-y-3">
            {WEIGHT_FIELDS.map((field) => (
              <label key={field.key} className="grid gap-1 text-sm">
                <span className="text-muted-foreground">{field.label}</span>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={rankingWeights[field.key]}
                  onChange={(event) =>
                    setRankingWeights((current) => ({
                      ...current,
                      [field.key]: Number(event.target.value),
                    }))
                  }
                  className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2"
                  data-testid={`settings-weight-${field.key}`}
                />
              </label>
            ))}

            <Button
              type="submit"
              size="sm"
              disabled={busy}
              className="min-h-11 w-full sm:w-auto"
              data-testid="settings-save-ranking-weights"
            >
              Save ranking weights
            </Button>
          </form>
        </section>

        <PromptTemplatesPanel />

        <AuditLogPanel />
      </div>
    </div>
  )
}
